import React, { useMemo, useRef } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import InsightsMatrixCountBubble from './InsightsMatrixCountBubble'
import InsightsClusterPopover from './InsightsClusterPopover'
import Icon from '../icon/Icon'
import Text from '../Text'
import { useAppState } from '../../context/AppContext'
import { personaKeyForZone, IMemberPlot, IZoneCount } from './InsightsMatrix'
import { ICompanyMember, ICompanyTerm } from '../../common/models/Company'

interface IProps {
  zoneCounts: IZoneCount[]
  zoneCountsPrev?: IZoneCount[]
  totalMembers: number
  memberById: Map<string, ICompanyMember>
  plotByMemberId: Map<string, IMemberPlot>
  plotByMemberIdPrev?: Map<string, IMemberPlot>
  compareActive?: boolean
  selectedTerm?: ICompanyTerm
  compareTo?: ICompanyTerm
  selectedZoneKey?: string
  onZoneClick: (zone: IZoneCount) => void
  onZoneClose: () => void
  onSelectMember?: (member: ICompanyMember) => void
}

const zoneKey = (zoneX: 0 | 1 | 2, zoneY: 0 | 1 | 2): string =>
  `${zoneX}_${zoneY}`

const InsightsMatrixHeatmapView: React.FC<IProps> = ({
  zoneCounts,
  zoneCountsPrev,
  totalMembers,
  memberById,
  plotByMemberId,
  plotByMemberIdPrev,
  compareActive,
  selectedTerm,
  compareTo,
  selectedZoneKey,
  onZoneClick,
  onZoneClose,
  onSelectMember,
}) => {
  const { t } = useTranslation()
  const { theme } = useAppState()
  const cellRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const yRows: Array<0 | 1 | 2> = [2, 1, 0]
  const xCols: Array<0 | 1 | 2> = [0, 1, 2]

  const zoneByKey = useMemo(() => {
    const map = new Map<string, IZoneCount>()
    for (const z of zoneCounts) map.set(zoneKey(z.zoneX, z.zoneY), z)
    return map
  }, [zoneCounts])

  const zoneByKeyPrev = useMemo(() => {
    const map = new Map<string, IZoneCount>()
    for (const z of zoneCountsPrev ?? []) map.set(zoneKey(z.zoneX, z.zoneY), z)
    return map
  }, [zoneCountsPrev])

  const selectedZone = selectedZoneKey
    ? zoneByKey.get(selectedZoneKey) ?? null
    : null

  const selectedMembers = useMemo<ICompanyMember[]>(() => {
    if (!selectedZone) return []
    return selectedZone.memberIds
      .map((id) => memberById.get(id))
      .filter((m): m is ICompanyMember => !!m)
  }, [selectedZone, memberById])

  const anchor = selectedZoneKey
    ? cellRefs.current[selectedZoneKey] ?? null
    : null

  return (
    <>
      <Grid>
        {yRows.map((zoneY) =>
          xCols.map((zoneX) => {
            const key = zoneKey(zoneX, zoneY)
            const zone = zoneByKey.get(key)
            const count = zone?.memberIds.length ?? 0
            const prevCount = zoneByKeyPrev.get(key)?.memberIds.length ?? 0
            const delta = count - prevCount
            return (
              <Cell key={key}>
                {count > 0 && zone && (
                  <CellInner>
                    <InsightsMatrixCountBubble
                      ref={(el) => {
                        cellRefs.current[key] = el
                      }}
                      count={count}
                      total={totalMembers}
                      selected={selectedZoneKey === key}
                      ariaLabel={`${count} ${t('general.people')} — ${t(
                        personaKeyForZone(zoneY, zoneX)
                      )}`}
                      onClick={() => onZoneClick(zone)}
                    />
                    {compareActive && delta !== 0 && (
                      <ZoneDelta $negative={delta < 0}>
                        <Icon
                          type="ArrowTopRight"
                          size={9}
                          color={
                            delta > 0
                              ? theme.colors.tertiary
                              : theme.colors.warning
                          }
                        />
                        <Text
                          textStyle="caption"
                          color={delta > 0 ? 'tertiary' : 'warning'}
                          tag="span"
                        >
                          {delta > 0 ? `+${delta}` : delta}
                        </Text>
                      </ZoneDelta>
                    )}
                  </CellInner>
                )}
                {count === 0 && compareActive && prevCount > 0 && (
                  <ZoneDelta $negative>
                    <Icon
                      type="ArrowTopRight"
                      size={9}
                      color={theme.colors.warning}
                    />
                    <Text textStyle="caption" color="warning" tag="span">
                      {-prevCount}
                    </Text>
                  </ZoneDelta>
                )}
              </Cell>
            )
          })
        )}
      </Grid>
      <InsightsClusterPopover
        anchor={anchor}
        isOpen={!!selectedZone && selectedMembers.length > 0}
        members={selectedMembers}
        plotByMemberId={plotByMemberId}
        plotByMemberIdPrev={plotByMemberIdPrev}
        compareActive={compareActive}
        selectedTerm={selectedTerm}
        compareTo={compareTo}
        segmentLabel={
          selectedZone
            ? t(personaKeyForZone(selectedZone.zoneY, selectedZone.zoneX))
            : undefined
        }
        onClose={onZoneClose}
        onShowDetails={onSelectMember}
      />
    </>
  )
}

export default InsightsMatrixHeatmapView

const Grid = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
`

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const CellInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

const ZoneDelta = styled.span<{ $negative: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 6px;
  border-radius: 999px;

  > span:first-child {
    margin-right: 0;
    transform: ${({ $negative }) => ($negative ? 'rotate(90deg)' : 'none')};
  }
`
